<ng-template #modalTemplate>
  <div
    class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50"
    style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9998;"
  >
    <div
      class="relative mx-4 w-full max-w-5xl rounded-2xl bg-white shadow-xl flex flex-col"
      style="position: relative; max-height: 90vh;"
      (click)="$event.stopPropagation()"
    >
      <!-- Sticky header -->
      <div class="flex items-start justify-between p-5 pb-3 border-b border-gray-100">
        <div>
          <h2 class="text-xl font-bold text-gray-900">Choose Your Plan</h2>
          <p class="mt-1 text-sm text-gray-500">Select a plan that works for your club. You can change anytime.</p>
        </div>
        <button
          type="button"
          class="text-gray-400 hover:text-gray-600 p-1"
          (click)="dismiss()"
        >
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Scrollable body -->
      <div class="flex-1 overflow-y-auto p-5">
        <!-- Loading -->
        <div *ngIf="loading" class="flex items-center justify-center py-16">
          <div class="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>

        <div *ngIf="!loading">
          <!-- Billing Toggle -->
          <div class="flex justify-center mb-6">
            <cricteams-billing-toggle
              [selectedInterval]="selectedInterval"
              [savingsPercent]="savingsPercent"
              (intervalChange)="onIntervalChange($event)"
            />
          </div>

          <!-- Plan Cards Grid -->
          <div class="grid grid-cols-1 gap-4" [ngClass]="{'md:grid-cols-2': displayPlans.length === 2, 'md:grid-cols-3': displayPlans.length >= 3}">
            <cricteams-plan-card
              *ngFor="let plan of displayPlans"
              [plan]="plan"
              [selectedInterval]="selectedInterval"
              [selectedCountry]="clubCountry"
              [isCurrentPlan]="isCurrentPlan(plan)"
              [ctaLabel]="getCtaLabel(plan)"
              [showCta]="true"
              [isPopular]="plan.level === 2"
              [ctaDisabled]="upgradingPlanLevel === plan.level || changingPlanLevel === plan.level"
              [promotionCodes]="promotionCodes"
              (ctaClick)="onUpgradeClick($event)"
            />
          </div>
        </div>
      </div>

      <!-- Sticky footer -->
      <div class="p-4 border-t border-gray-100 text-center">
        <button
          type="button"
          class="text-sm text-gray-500 hover:text-gray-700 underline"
          (click)="dismiss()"
        >
          Maybe later
        </button>
      </div>
    </div>
  </div>

  <!-- Upgrade confirmation dialog (Paid → Higher Paid) — rendered inside the portal -->
  @if (showUpgradeConfirm && upgradePreview) {
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      style="position: fixed; inset: 0; z-index: 9999;"
      (click)="closeUpgradeConfirm()"
    >
      <div
        class="mx-4 w-full max-w-sm rounded-lg bg-white p-5 shadow-xl"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-center gap-3 mb-3">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100">
            <svg class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          </div>
          <h3 class="text-sm font-semibold text-gray-900">Upgrade to {{ upgradePreview.targetPlanName }}?</h3>
        </div>

        <div class="space-y-2">
          @if (upgradePreview.amountDue !== undefined && upgradePreview.amountDue !== null) {
            <p class="text-xs text-gray-600 leading-relaxed">
              You'll be charged a prorated amount of
              <span class="font-semibold text-gray-900">
                {{ upgradePreview.amountDue | currency: (upgradePreview.currency ?? currencyCode).toUpperCase() : 'symbol-narrow' : '1.2-2' }}
              </span>
              today.
            </p>
          }
          <p class="text-xs text-gray-600 leading-relaxed">
            Your new limits and features take effect immediately. No need to re-enter payment details.
          </p>
        </div>

        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            [disabled]="confirmingUpgrade"
            (click)="closeUpgradeConfirm()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            [disabled]="confirmingUpgrade"
            (click)="confirmUpgrade()"
          >
            {{ confirmingUpgrade ? 'Upgrading...' : 'Confirm Upgrade' }}
          </button>
        </div>
      </div>
    </div>
  }
</ng-template>

<!-- Payment modal (z-9999, on top of upgrade modal z-9998) -->
<cricteams-payment-modal
  #paymentModal
  [clubId]="clubId"
  [selectedInterval]="selectedInterval"
  [selectedCountry]="clubCountry"
  (subscriptionChanged)="onSubscriptionChanged()"
  (closed)="onPaymentClosed()"
/>
